import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import { sagittalFromLandmarks } from './hip-displacement-horizontal-strategy';
import { logPostureClassificationDebug } from './posture-classification-debug';
import type { Landmark } from '@/components/posture-analysis/types';
import { IDX } from '@/components/posture-analysis/constants';

const STRATEGY_ID = 'simplifiedAutoPilot';
const STRATEGY_NAME = 'Simplified Auto-Pilot Strategy';
const DEBUG_LOG_PREFIX = '[simplifiedAutoPilot]';

interface VetoThresholds {
  slrSwaybackVetoMaxDeg: number;
  kneeFlexionExtremeDegMin: number;
  fullHipExtensionDeg: number;
  oberNeutralDeg: number;
  kneeHyperextensionExtremeDegMin: number;
  pelvicTiltVetoDegMin: number;
  overridePointBoost: number;
  indicatorPointWeight: number;
}

const DEFAULT_VETO_THRESHOLDS: VetoThresholds = {
  slrSwaybackVetoMaxDeg: 45,
  kneeFlexionExtremeDegMin: 10,
  fullHipExtensionDeg: 0,
  oberNeutralDeg: 0,
  kneeHyperextensionExtremeDegMin: 5.0,
  pelvicTiltVetoDegMin: 15.0,
  overridePointBoost: 3,
  indicatorPointWeight: 4,
};

function readVetoThresholds(rules: any): VetoThresholds {
  const v = rules?.vetoExclusion ?? {};
  return {
    slrSwaybackVetoMaxDeg: v.slrSwaybackVetoMaxDeg ?? DEFAULT_VETO_THRESHOLDS.slrSwaybackVetoMaxDeg,
    kneeFlexionExtremeDegMin:
      v.kneeFlexionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeFlexionExtremeDegMin,
    fullHipExtensionDeg: v.fullHipExtensionDeg ?? DEFAULT_VETO_THRESHOLDS.fullHipExtensionDeg,
    oberNeutralDeg: v.oberNeutralDeg ?? DEFAULT_VETO_THRESHOLDS.oberNeutralDeg,
    kneeHyperextensionExtremeDegMin:
      v.kneeHyperextensionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeHyperextensionExtremeDegMin,
    pelvicTiltVetoDegMin: v.pelvicTiltVetoDegMin ?? DEFAULT_VETO_THRESHOLDS.pelvicTiltVetoDegMin,
    overridePointBoost: v.overridePointBoost ?? DEFAULT_VETO_THRESHOLDS.overridePointBoost,
    indicatorPointWeight: v.indicatorPointWeight ?? DEFAULT_VETO_THRESHOLDS.indicatorPointWeight,
  };
}

const sumDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) : null;
};

const avgDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) / finite.length : null;
};

const bothAtLeast = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a >= threshold &&
  b >= threshold;

const bothAtMost = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a <= threshold &&
  b <= threshold;

const rad2deg = (r: number) => (r * 180) / Math.PI;

function angleAtLandmark(a: Landmark, b: Landmark, c: Landmark): number {
  if (!a || !b || !c) return NaN;
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mab = Math.hypot(ab.x, ab.y);
  const mcb = Math.hypot(cb.x, cb.y);
  if (mab === 0 || mcb === 0) return NaN;
  const cos = Math.min(1, Math.max(-1, dot / (mab * mcb)));
  return rad2deg(Math.acos(cos));
}

export const simplifiedAutoPilotStrategy: PostureOrientationClassificationStrategy = {
  id: STRATEGY_ID,
  displayName: STRATEGY_NAME,
  maxClassificationPoints: 20, // 5 potential indicators * 4 points

  classify(
    input: PostureClassificationStrategyInput,
    context: PostureClassificationStrategyContext
  ): PostureClassificationStrategyResult | null {
    const rulesBundle = getEvaluationRules() as any;
    const rules = rulesBundle.posture.thresholds;
    const veto = readVetoThresholds(rules);

    const kneeThresholds = {
      kneeFlexionDegMin: rules.kneeFlexionDegMin,
      kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
    };
    const parsed = parseLateralPostureMetrics(input.analysisData, kneeThresholds);
    if (!parsed) return null;

    const fromLm = sagittalFromLandmarks(
      input,
      context,
      kneeThresholds.kneeFlexionDegMin,
      kneeThresholds.kneeHyperextensionDegMin
    );

    const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
    const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
    const kneeHyperext = fromLm ? fromLm.kneeHyperext : parsed.isKneeHyperext && !parsed.isKneeNeutral;
    const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
    const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;

    const kneeFlexDeg =
      fromLm?.kneeFlexEvidenceDeg ??
      (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : NaN);

    let kneeHyperextDeg = NaN;
    if (fromLm && input.sagittal?.landmarks?.length) {
      const s = input.sagittal;
      const side = s.sideView;
      const idxLR = (l: number, r: number) => (side === 'Left' ? l : r);
      const H = s.landmarks[idxLR(IDX.L_HIP, IDX.R_HIP)];
      const K = s.landmarks[idxLR(IDX.L_KNEE, IDX.R_KNEE)];
      const A = s.landmarks[idxLR(IDX.L_ANK, IDX.R_ANK)];
      if (H && K && A && Math.abs(A.y - H.y) > 10) {
        const kneeRawAngle = angleAtLandmark(H, K, A);
        const kneeDeg = isFinite(kneeRawAngle) ? Math.abs(180 - kneeRawAngle) : NaN;
        kneeHyperextDeg = kneeDeg;
      }
    }
    if (!isFinite(kneeHyperextDeg)) {
      kneeHyperextDeg = parsed.maxKneeHyperextDeg;
    }

    const isPinned =
      !!context.asisPsisLine ||
      (!!input.sagittal?.custom?.ASIS && !!input.sagittal?.custom?.PSIS) ||
      (isFinite(pelvicTilt) && !parsed.isPelvicTiltEstimated);

    const sop = input.sopMetrics;

    // R1 — Ober's / hip-extension full bilaterally → no SB/APT compensation possible → Stacked.
    if (sop) {
      const hipExtensionFullBilateral = bothAtLeast(
        sop.hipExtensionLeft,
        sop.hipExtensionRight,
        veto.fullHipExtensionDeg
      );
      const obersNeutralBilateral = bothAtMost(sop.oberLeft, sop.oberRight, veto.oberNeutralDeg);
      if (hipExtensionFullBilateral || obersNeutralBilateral) {
        const r1Evidence =
          `[VETO] SoP: full hip extension / neutral Ober's bilaterally → no SB/APT compensation → Stacked`;
        logPostureClassificationDebug(DEBUG_LOG_PREFIX, 'simplifiedAutoPilot classification', {
          orientation: 'Stacked',
          decisionPath: 'r1ForceStacked',
          hasSopMetrics: true,
          indicatorEvidence: [r1Evidence],
        });
        return {
          orientation: 'Stacked',
          confidencePct: 100,
          matchedPoints: 0,
          maxPoints: 0,
          strategyId: STRATEGY_ID,
          strategyDisplayName: STRATEGY_NAME,
          evidence: [r1Evidence],
        };
      }
    }

    const indicatorWeight = veto.indicatorPointWeight;
    let sbIndicatorPoints = 0;
    let aptIndicatorPoints = 0;
    const indicatorEvidence: string[] = [];
    let evaluableIndicatorsCount = 0;

    // 1. Extreme Knee Flexion & Hyperextension indicators
    const pelvicTiltDegMin =
      input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;
    const isKneeStateEvaluable =
      isFinite(parsed.maxKneeFlexDeg) || isFinite(parsed.maxKneeHyperextDeg);
    if (isKneeStateEvaluable) {
      evaluableIndicatorsCount++;
      if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > veto.kneeFlexionExtremeDegMin) {
        sbIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Extreme knee flexion (~${kneeFlexDeg.toFixed(0)}° > ${veto.kneeFlexionExtremeDegMin}°) → +${indicatorWeight} Swayback`
        );
      }
      if (kneeHyperext && isFinite(kneeHyperextDeg) && kneeHyperextDeg >= veto.kneeHyperextensionExtremeDegMin) {
        aptIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Extreme knee hyperextension (~${kneeHyperextDeg.toFixed(0)}° ≥ ${veto.kneeHyperextensionExtremeDegMin}°) → +${indicatorWeight} APT`
        );
      }
    }

    // 2. Pelvic tilt ≥ 15° (pinned ASIS/PSIS only)
    const isPelvicTiltEvaluable = isPinned && isFinite(pelvicTilt);
    if (isPelvicTiltEvaluable) {
      evaluableIndicatorsCount++;
      if (pelvicTilt >= veto.pelvicTiltVetoDegMin) {
        aptIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Pelvic tilt ≥ ${veto.pelvicTiltVetoDegMin}° with pinned ASIS/PSIS (~${pelvicTilt.toFixed(1)}°) → +${indicatorWeight} APT`
        );
      }
    }

    // SoP-based indicators
    // 3. Femoral rotation asymmetry (R2/R3)
    const irTotal = sop ? sumDefined(sop.femoralIrLeft, sop.femoralIrRight) : null;
    const erTotal = sop ? sumDefined(sop.femoralErLeft, sop.femoralErRight) : null;
    const isFemoralRotationEvaluable = irTotal !== null && erTotal !== null;
    if (isFemoralRotationEvaluable) {
      evaluableIndicatorsCount++;
      if (irTotal! > erTotal!) {
        aptIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] SoP: femoral IR (${irTotal!.toFixed(0)}°) > ER (${erTotal!.toFixed(0)}°) → +${indicatorWeight} APT`
        );
      } else if (erTotal! > irTotal!) {
        sbIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] SoP: femoral ER (${erTotal!.toFixed(0)}°) > IR (${irTotal!.toFixed(0)}°) → +${indicatorWeight} Swayback`
        );
      }
    }

    // 5. Torso offset (lean)
    const hasTorsoOffsetInput =
      (fromLm && isFinite(fromLm.torsoOffsetCm)) ||
      Object.values(input.analysisData).some(
        (view) => view && ('Hip Anterior Shift' in view || 'Shoulder Anterior Shift' in view)
      );
    const isTorsoOffsetEvaluable = isFinite(torsoOffsetCm) && hasTorsoOffsetInput;
    if (isTorsoOffsetEvaluable) {
      evaluableIndicatorsCount++;
      if (torsoOffsetCm > rules.strictLean) {
        aptIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Image: shoulders forward of hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm > ${rules.strictLean} cm) → +${indicatorWeight} APT`
        );
      } else if (torsoOffsetCm < -rules.strictLean) {
        sbIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Image: shoulders behind hips / “C” curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm < -${rules.strictLean} cm) → +${indicatorWeight} Swayback`
        );
      }
    }

    if (evaluableIndicatorsCount === 0) {
      return null;
    }

    const maxPoints = evaluableIndicatorsCount * indicatorWeight;

    // Hard exclusions — nullify opposing pattern points before tie-breakers.
    let isSwaybackExcluded = false;
    let isAptExcluded = false;
    const vetoEvidence: string[] = [];

    if (sop) {
      // R2 / R3 — femoral IR vs ER asymmetry (bilateral totals).
      if (irTotal !== null && erTotal !== null) {
        if (irTotal > erTotal) {
          isSwaybackExcluded = true;
          vetoEvidence.push(
            `[VETO] SoP: femoral IR (${irTotal.toFixed(0)}°) > ER (${erTotal.toFixed(0)}°) → excludes Swayback`
          );
        } else if (erTotal > irTotal) {
          isAptExcluded = true;
          vetoEvidence.push(
            `[VETO] SoP: femoral ER (${erTotal.toFixed(0)}°) > IR (${irTotal.toFixed(0)}°) → excludes APT`
          );
        }
      }

      // R4 — high SLR excludes Swayback (does not vote APT).
      const avgSlr = avgDefined(sop.slrLeft, sop.slrRight);
      if (avgSlr !== null && avgSlr > veto.slrSwaybackVetoMaxDeg) {
        isSwaybackExcluded = true;
        vetoEvidence.push(
          `[VETO] SoP: SLR (${avgSlr.toFixed(0)}°) > ${veto.slrSwaybackVetoMaxDeg}° → excludes Swayback`
        );
      }
    }

    // Pelvic tilt ≥ 15° with pinned ASIS/PSIS excludes Swayback.
    if (isPinned && isFinite(pelvicTilt) && pelvicTilt >= veto.pelvicTiltVetoDegMin) {
      isSwaybackExcluded = true;
      vetoEvidence.push(
        `[VETO] Image: pelvic tilt ≥ ${veto.pelvicTiltVetoDegMin}° with pinned ASIS/PSIS (~${pelvicTilt.toFixed(1)}°) → excludes Swayback`
      );
    }

    const effectiveSbPoints = isSwaybackExcluded ? 0 : sbIndicatorPoints;
    const effectiveAptPoints = isAptExcluded ? 0 : aptIndicatorPoints;

    // Decision logic
    let orientation: PostureOrientationLabel = 'Stacked';
    let matched = 0;
    let winnerEvidence: string[] = [];
    let decisionPath = '';

    if (effectiveSbPoints === 0 && effectiveAptPoints === 0) {
      orientation = 'Stacked';
      matched = 0;
      winnerEvidence = [];
      decisionPath = 'noIndicators.stacked';
    } else if (effectiveSbPoints > effectiveAptPoints) {
      orientation = 'Swayback';
      matched = effectiveSbPoints;
      winnerEvidence = [];
      decisionPath = 'sbWinner';
    } else if (effectiveAptPoints > effectiveSbPoints) {
      orientation = 'Anterior Pelvic Tilt';
      matched = effectiveAptPoints;
      winnerEvidence = [];
      decisionPath = 'aptWinner';
    } else {
      // Tie-breaker — only when neither pattern is excluded
      if (hipFwdCm >= rules.hipForwardCmMin && effectiveSbPoints > 0) {
        orientation = 'Swayback';
        matched = effectiveSbPoints;
        winnerEvidence = ['Tie-break: large hip–ankle offset favors swayback shift.'];
        decisionPath = 'tieBreak.hipFwdFavorsSwayback';
      } else {
        orientation = 'Anterior Pelvic Tilt';
        matched = effectiveAptPoints;
        winnerEvidence = ['Tie-break: hip stack not dominant → anterior orientation.'];
        decisionPath = 'tieBreak.hipStackFavorsApt';
      }
    }

    const evidence = [...indicatorEvidence, ...vetoEvidence, ...winnerEvidence];

    const confidencePct =
      orientation === 'Stacked'
        ? Math.min(
            100,
            Math.round(
              (1 - Math.max(sbIndicatorPoints, aptIndicatorPoints) / Math.max(1, maxPoints * 2)) *
                100
            )
          )
        : Math.min(100, Math.round((matched / maxPoints) * 100));

    logPostureClassificationDebug(DEBUG_LOG_PREFIX, 'simplifiedAutoPilot classification', {
      sbIndicatorPoints,
      aptIndicatorPoints,
      isSwaybackExcluded,
      isAptExcluded,
      effectiveSbPoints,
      effectiveAptPoints,
      orientation,
      decisionPath,
      hasSopMetrics: !!input.sopMetrics,
      indicatorEvidence,
      vetoEvidence,
    });

    return {
      orientation,
      confidencePct,
      matchedPoints: matched,
      maxPoints,
      strategyId: STRATEGY_ID,
      strategyDisplayName: STRATEGY_NAME,
      evidence,
    };
  },
};
